import { t } from 'i18next';
import { Component, ReactElement } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from '../../../../../../shared/common/types/common';
import { safeParseInt } from '../../../../../../shared/common/utils';
import { TError, createActionSetError } from '../../../../../../shared/frontend/scripts/actions/errorActions';
import KeystoneActionCreator from '../../actions/keystoneActionCreator';
import { TKeystoneRunRankingsClientState, TKeystoneRunRankingsServerState, TApplicationState } from '../../libs/store';
import { areFieldsDifferent } from '../../../../../../shared/frontend/libs/utils';
import apiService from '../../services/apiService';
import { TGetKeystoneRunRankingsParams } from '../../../../../../shared/frontend/scripts/services/commonApiService';
import { KeystoneRunRankingsTable } from '../presentational/keystoneRunRankingsElements/KeystoneRunRankingsTable';
import { Spinner } from '../../../../../../shared/frontend/components/presentational/spinner';
import frontendLog from '../../libs/frontendLog';
import Pagination from '../../../../../../shared/frontend/components/Pagination';
import { LinkSharingSpecGuideAd } from '../../../../../../shared/frontend/components/ads/linkSharingSpecGuideAd';

type TPropsFromState = any; // FIXME: type this!

type TPropsThatDispatch = {
  setKeystoneRunRankings(instanceId: string, actionData: TKeystoneRunRankingsClientState): void;
  showError(actionData: TError): void;
  showLoadingSpinner(instanceId: string, show: boolean): void;
  setKeystoneRankingsError(instanceId: string, error: any): void;
};

type TOwnProps = {
  season: string;
  dungeon: string;
  region: string;
  page: number;
  type?: TLeaderboardType;
  affixes?: string[];
  limit?: number;
  minMythicLevel?: number;
  maxMythicLevel?: number;
  eventId?: number;
  platoonNamespace?: string;
  columns?: any;
  teamDetailsRoute?: string;
  expandableCharacterRow?: boolean;
  navButtonsProps: (numEntries: number, currentPage: number, lastPage: number) => Record<string, any>;
};

export type TKeystoneRunRankingsProps = TOwnProps &
  TPropsFromState &
  TPropsThatDispatch &
  RouteComponentProps<TKeystoneRunRankingsRouteParams, any>;

function typed<T>(v: T): T {
  return v;
}

type TLeaderboardType = 'leaderboards' | 'leaderboards-strict';
type TKeystoneRunRankingsRouteParams = {
  season: string;
  dungeon: string;
  region: string;
  page: number;
  type?: TLeaderboardType;
  affixes?: string[];
};

/**
 * KeystoneRunRankingsContent
 */
class KeystoneRunRankingsContent extends Component<TKeystoneRunRankingsProps, {}> {
  public render(): ReactElement<TKeystoneRunRankingsProps> {
    const navButtonsProps = this.props.navButtonsProps
      ? this.props.navButtonsProps(
          this.props.rankedGroups?.length || 0,
          this.props.ui?.page || 0,
          this.props.ui?.lastPage || 0
        )
      : null;

    return (
      <div>
        {(!this.props.rankedGroups || this.props.isLoading) && <Spinner inline />}

        {this.props.rankedGroups && (
          <Pagination
            top={!!navButtonsProps}
            bottom={!!navButtonsProps}
            navButtonsProps={navButtonsProps}
            searchConfig={this.props.searchConfig}
            upperChildren={
              <LinkSharingSpecGuideAd
                class={this.props.ui.class}
                season={this.props.ui.season}
                className="rio-hidden--small"
              />
            }
          >
            <KeystoneRunRankingsTable
              wrapMembersOnOverflow
              allSubRegions={this.props.allSubRegions}
              season={this.props.season}
              title={this.props.title}
              dungeon={this.props.dungeon}
              region={this.props.region}
              expandableCharacterRow={this.props.expandableCharacterRow}
              groups={this.props.limit ? this.props.rankedGroups.slice(0, this.props.limit) : this.props.rankedGroups}
              titleSpecifiers={[]}
              platoonNamespace={this.props.platoonNamespace}
              columns={this.props.columns}
              teamDetailsRoute={this.props.teamDetailsRoute}
              component={this.props.component}
              affixes={this.props.affixes}
              bracketId={this.props.bracketId}
              eventNamespace={this.props.eventNamespace}
            />
          </Pagination>
        )}
      </div>
    );
  }
}

// take state from store and convert into props
function mapStateToProps(state: TApplicationState, ownProps: any): TPropsFromState {
  return {
    allSubRegions: state.siteMeta.subRegions,
    ...state.keystoneRankings.instances[ownProps.instanceId || 'main']
  };
}

// return props that need access to `dispatch` function (props that can modify the store?)
function mapDispatchToProps(dispatch: Function, ownProps: TKeystoneRunRankingsProps): TPropsThatDispatch {
  return {
    setKeystoneRunRankings(instanceId: string, rankings: TKeystoneRunRankingsClientState) {
      dispatch(KeystoneActionCreator.setKeystoneRankings(instanceId, rankings));
    },
    showError(actionData: TError) {
      dispatch(createActionSetError(actionData));
    },
    showLoadingSpinner(instanceId: string, show: boolean) {
      dispatch(KeystoneActionCreator.showLoadingSpinner(instanceId, show));
    },
    setKeystoneRankingsError(instanceId: string, error: any) {
      dispatch(KeystoneActionCreator.setKeystoneRankingsError(instanceId, error));
    }
  };
}

// return one entire set of props
function mergeProps(stateProps: TPropsFromState, dispatchProps: TPropsThatDispatch, ownProps: any) {
  const loadParams: TGetKeystoneRunRankingsParams = {
    season: ownProps.season,
    dungeon: ownProps.dungeon,
    region: ownProps.region || 'world',
    strict: ownProps.type === typed<TLeaderboardType>('leaderboards-strict'),
    affixes: ownProps.affixes,
    page: safeParseInt(ownProps.page, 0),
    limit: safeParseInt(ownProps.limit, 0),
    search: ownProps.search || '',
    minMythicLevel: safeParseInt(ownProps.minMythicLevel, 0),
    maxMythicLevel: safeParseInt(ownProps.maxMythicLevel, 0),
    eventId: safeParseInt(ownProps.eventId, 0),
    faction: ownProps.faction || '',
    realm: ownProps.realm || '',
    period: ownProps.period || 0,
    recent: ownProps.recent || false
  };

  const checkFields = [
    'season',
    'dungeon',
    'region',
    'strict',
    'affixes',
    'page',
    'limit',
    'minMythicLevel',
    'maxMythicLevel',
    'eventId',
    'faction',
    'realm',
    'period',
    'recent',
    'search'
  ];

  const contentChanged = !stateProps.ui || areFieldsDifferent(checkFields, loadParams, stateProps.ui);
  const instanceId = ownProps.instanceId || 'main';

  if (!stateProps.isLoading && (!stateProps.rankedGroups || contentChanged) && !stateProps.isError) {
    dispatchProps.showLoadingSpinner(instanceId, true);

    apiService.getKeystoneRunRankings(loadParams, (err: any, result?: TKeystoneRunRankingsServerState) => {
      if (err) {
        dispatchProps.showError({
          title: t('common:errors.something-went-wrong'),
          body: err.toString()
        });
        frontendLog.error({ err }, 'getKeystoneRunRankings failed');
        dispatchProps.setKeystoneRankingsError(instanceId, err);
        dispatchProps.showLoadingSpinner(instanceId, false);
        return;
      }

      if (!result?.rankings) {
        dispatchProps.showError({
          title: t('common:errors.something-went-wrong'),
          body: t('common:errors.unable-to-load-data-reload')
        });
        dispatchProps.setKeystoneRankingsError(instanceId, {
          title: t('common:errors.something-went-wrong'),
          body: t('common:errors.unable-to-load-data-reload')
        });
        frontendLog.error('getKeystoneRunRankings got empty result');
        dispatchProps.showLoadingSpinner(instanceId, false);
        return;
      }

      dispatchProps.setKeystoneRunRankings(instanceId, { ...result.rankings });
      dispatchProps.showLoadingSpinner(instanceId, false);
    });
  }

  return {
    ...ownProps,
    ...stateProps,
    ...dispatchProps
  };
}

export default connect<TPropsFromState, TPropsThatDispatch, TKeystoneRunRankingsProps>(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps
)(KeystoneRunRankingsContent);
